/**
 * How a memo version is named, everywhere it is named (DOP-659).
 *
 * A version a person named reads by that name, with its number beside it in
 * smaller text; a version nobody named reads by its number alone. The header,
 * the deal list, the Versions tab and the chat chip all say it this way, so
 * "v6_v2" in one place is never "v9" in another.
 */

import React from "react";

export interface MemoVersionRef {
	version: number;
	name?: string | null;
}

const named = (ref: MemoVersionRef): string | null => ref.name?.trim() || null;

/** "After CFO call (v3)", or "v3" for a version nobody named. */
export const versionText = (ref: MemoVersionRef): string => {
	const name = named(ref);
	return name ? `${name} (v${ref.version})` : `v${ref.version}`;
};

/** "After CFO call (v3)", or "memo v3": the form a sentence about a deal uses. */
export const memoVersionText = (ref: MemoVersionRef): string =>
	named(ref) ? versionText(ref) : `memo v${ref.version}`;

interface VersionNameProps extends MemoVersionRef {
	/** What an unnamed version reads as before its number ("Your edits"). */
	fallback?: string;
	/** Set on the name, when there is one. */
	nameTestId?: string;
}

/** The name, then the number in smaller text; the number alone when unnamed. */
export const VersionName: React.FC<VersionNameProps> = ({ version, name, fallback, nameTestId }) => {
	const shown = named({ version, name });
	if (!shown && !fallback) return <>v{version}</>;
	return (
		<>
			<span className="min-w-0 truncate" data-testid={shown ? nameTestId : undefined}>
				{shown ?? fallback}
			</span>{" "}
			<span className="text-[0.85em] font-normal opacity-75">v{version}</span>
		</>
	);
};
